/**
 * Money In: what customers paid, what MTN kept, what landed in the wallet.
 *
 * The wallet dashboard used to show money OUT only (payouts, batches,
 * refunds); the only trace of money IN was a gross figure under Reports.
 * MTN's deduction on collections appeared nowhere, so the 15 Oct 2026 ticket
 * payment (35,000 UGX arriving as 34,300) was flagged as a mismatch.
 *
 * Reads two wallet endpoints (moneyInService, the same figures the nightly
 * reconciliation uses):
 *   summary  by kind (customer payments by rail, delivery fees, rider
 *            deposits, float top-ups, adjustments): gross, MTN fee, landed
 *   list     customer payments held in escrow, one row per vendor allocation
 * A row whose fee was recorded at hold time says so; a row from before fees
 * were recorded is estimated at the live tariff and marked as such.
 *
 * Delivery offers (first-order and referral free delivery, 2 Nov 2026): the
 * part of a delivery fee Lusuku paid on the customer's behalf is shown beside
 * the delivery fees customers paid, labelled as funded by Lusuku and marked
 * "not money in": it is a promotional cost, no cash moved, and it is never
 * part of the money in, MTN or landed totals (the wallet keeps it out).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiDownload, FiPercent, FiRefreshCw, FiSearch } from "react-icons/fi";
import CopyValue from "../ui/CopyValue";
import AppButton from "../ui/AppButton";
import {
  apiWalletMoneyInSummary,
  apiWalletMoneyInUnexplained,
  apiWalletListMoneyIn,
  apiWalletUpdateCollectionFee,
  apiGetVendorCommissionContracts,
} from "../../api/index.js";
import {
  C, S, fmtUGX, fmtNum, fmtDay, fmtClock, truncate, CellStack, VendorCell, DateTimeCell, StatCard, SkeletonRows,
  quickRange, rangeToIso, localDateStr,
} from "./walletUi";
// The tariff's tooltip lives with the other Payments & money settings (Settings/help/paymentsHelp.js).
import SettingHelp from "../../views/Settings/settingsHelp";

// GROUP: an Order together order, held from the members' contributions.
export const RAIL_LABEL = { MOMO: "MTN MoMo", FLW: "Flutterwave", COD: "Cash on delivery", GROUP: "Group order", UNTAGGED: "Not tagged" };
export const RAIL_TONE = { MOMO: "yellow", FLW: "purple", COD: "green", GROUP: "blue", UNTAGGED: "neutral" };
export const ESCROW_TONE = { HELD: "blue", RELEASED: "green", REVERSED: "red" };
export const FEE_BASIS_LABEL = { RECORDED: "recorded", ESTIMATED: "estimated", NONE: "no fee" };

const QUICK = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["week", "This week"],
  ["month", "This month"],
  ["lastMonth", "Last month"],
];
const PAGE_SIZE = 25;

/** 256779991454 reads as 0779 991 454, the way the number is dialled here. */
export function formatMsisdn(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  const local = digits.length > 9 ? `0${digits.slice(-9)}` : digits;
  return local.length === 10 ? `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}` : local;
}

/** Rows of the by-kind table, in reading order (pure). */
export function kindRows(summary) {
  if (!summary?.kinds) return [];
  const k = summary.kinds;
  const rows = [];
  const rails = Object.keys(k.customerPayments?.byRail || {}).sort((a, b) => {
    const order = ["MOMO", "FLW", "COD", "UNTAGGED"];
    return order.indexOf(a) - order.indexOf(b);
  });
  /* Goods and delivery are the two halves of what a customer is charged, and
     they are named as such so the two rows visibly add up to the payment
     (30,000 + 6,500 = the 36,500 a customer paid, 17 Oct 2026). The rail rows
     under the goods are a breakdown of that one figure, not further money:
     they read "of which" and are shown only when there is more than one rail. */
  const goods = k.customerPayments;
  const single = rails.length === 1 ? rails[0] : null;
  rows.push({
    key: "customerPayments",
    label: single ? `Goods paid by customers (${RAIL_LABEL[single] || single})` : "Goods paid by customers",
    ...goods,
    group: true,
  });
  if (!single) {
    for (const rail of rails) {
      rows.push({ key: `customerPayments:${rail}`, label: `of which ${RAIL_LABEL[rail] || rail}`, indent: true, breakdown: true, ...goods.byRail[rail] });
    }
  }
  rows.push({ key: "deliveryFees", label: "Delivery fees paid by customers", ...k.deliveryFees });
  /* The other half of a free or reduced delivery: what Lusuku paid toward
     the trip. Shown so the two parts of the fee read together, and marked
     as no cash in so nobody adds it to the figures above or below. */
  const funded = k.deliveryOffersFunded;
  if (funded && (funded.count || funded.gross)) {
    rows.push({
      key: "deliveryOffersFunded",
      label: "Delivery fees funded by Lusuku (customer offers)",
      count: funded.count,
      gross: funded.gross,
      fee: 0,
      net: 0,
      nonCash: true,
      breakdown: true,
    });
  }
  /* Order together: each member's share of a group order is money in on its
     own rail (the server counts these, never the group order's own hold, so
     nothing here is counted twice). Shown only in a period that had some. */
  const shares = k.groupContributions;
  if (shares && shares.count) {
    const shareRails = Object.keys(shares.byRail || {});
    const oneRail = shareRails.length === 1 ? shareRails[0] : null;
    rows.push({
      key: "groupContributions",
      label: oneRail ? `Group order shares paid by members (${RAIL_LABEL[oneRail] || oneRail})` : "Group order shares paid by members",
      ...shares,
    });
    if (!oneRail) {
      for (const rail of shareRails) {
        rows.push({ key: `groupContributions:${rail}`, label: `of which ${RAIL_LABEL[rail] || rail}`, indent: true, breakdown: true, ...shares.byRail[rail] });
      }
    }
  }
  rows.push({ key: "riderDeposits", label: "Rider cash deposits", ...k.riderDeposits });
  rows.push({ key: "floatTopUps", label: "Float top-ups", ...k.floatTopUps });
  if (k.adjustments?.count) rows.push({ key: "adjustments", label: "Adjustments", ...k.adjustments });
  if (k.mtnInterest?.count) rows.push({ key: "mtnInterest", label: "Interest credited by MTN", ...k.mtnInterest });
  return rows;
}

/** The sentence under the tariff line (pure). */
export function tariffNote(tariff) {
  if (!tariff) return "";
  const pct = Number(tariff.ratePct);
  const where = tariff.source === "setting"
    ? `set on this dashboard${tariff.updatedBy ? ` by ${tariff.updatedBy}` : ""}`
    : tariff.source === "env"
      ? "set in the service configuration"
      : "the observed default";
  return `MTN keeps ${pct}% of every MoMo collection before it reaches the wallet (${where}). The nightly reconciliation checks this against the live balance.`;
}

// "all" is everything since the wallet went live; otherwise a quick-range key.
const rangeForKey = (key) => (key === "all" ? { from: "2026-01-01", to: quickRange("today").to } : quickRange(key));

// `rangeKey` is a quick-range key or "all"; a change of `focusToken` re-applies
// it (the MoMo Balance card opens this tab on everything, every time).
export default function MoneyInTab({ isSuperAdmin = false, rangeKey = "month", focusToken = 0, onRecordTopUp }) {
  const [range, setRange] = useState(() => rangeForKey(rangeKey));
  useEffect(() => { setRange(rangeForKey(rangeKey)); setPage(1); }, [rangeKey, focusToken]);
  // Days the MTN wallet moved with nothing in the books to explain it.
  const [unexplained, setUnexplained] = useState(null);
  const [rail, setRail] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ records: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [tariffModal, setTariffModal] = useState(false);
  const [tariffForm, setTariffForm] = useState({ ratePct: "", reason: "" });
  const [tariffSaving, setTariffSaving] = useState(false);

  // Every load gets a sequence number and only the latest may write state.
  // Before this, a slower reply for an earlier range could land after a
  // faster one and leave "This month" figures under a picker that said
  // "Yesterday" (seen 31 Oct 2026: 36,559 shown for a day with no inflows).
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const iso = rangeToIso(range);
      const [sumRes, listRes, unexplainedRes] = await Promise.all([
        apiWalletMoneyInSummary(iso),
        apiWalletListMoneyIn({ ...iso, rail: rail || undefined, status: status || undefined, search: query || undefined, page, limit: PAGE_SIZE }),
        Promise.resolve().then(() => apiWalletMoneyInUnexplained(iso)).catch(() => ({ data: null })), // older wallet deploys: no panel
      ]);
      if (seq !== loadSeq.current) return; // a newer range has been asked for since
      setSummary(sumRes.data);
      setList(listRes.data);
      setUnexplained(unexplainedRes?.data ?? null);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      const status404 = err?.response?.status === 404;
      setError(status404
        ? "Money In needs the updated wallet service."
        : (err?.response?.data?.error || err?.message || "Could not load money in."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [range, rail, status, query, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    apiGetVendorCommissionContracts({})
      .then((res) => { if (!cancelled) setContracts(res.data?.contracts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const vendorName = useMemo(() => {
    const map = new Map();
    for (const c of contracts) {
      if (c.vendorId) map.set(String(c.vendorId), c);
      if (c.contractId) map.set(String(c.contractId), c);
    }
    return (id) => map.get(String(id));
  }, [contracts]);

  const openTariff = () => {
    setTariffForm({ ratePct: summary?.tariff?.ratePct ?? "", reason: "" });
    setTariffModal(true);
  };

  const saveTariff = async () => {
    const n = Number(tariffForm.ratePct);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      toast.error("Enter the percentage MTN keeps, between 0 and 100.");
      return;
    }
    setTariffSaving(true);
    try {
      await apiWalletUpdateCollectionFee({ ratePct: n, reason: tariffForm.reason || undefined });
      toast.success(`MTN collection tariff set to ${n}%`);
      setTariffModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save the tariff.");
    } finally {
      setTariffSaving(false);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const rows = kindRows(summary);
  const momo = summary?.momo;
  const total = summary?.total;
  const pagination = list.pagination || { page: 1, pages: 1, total: 0 };
  const estimatedCount = summary?.feeBasis?.estimated || 0;
  /* What customers were charged in the period: goods held for vendors plus
     the delivery fees paid with them. 30,000 + 6,500 = the 36,500 on the
     customer's own SMS (17 Oct 2026). */
  const goods = summary?.kinds?.customerPayments;
  const delivery = summary?.kinds?.deliveryFees;
  // Group order shares are what members of an Order together basket paid
  // for their goods and their part of the delivery: charged to customers too.
  const groupShares = summary?.kinds?.groupContributions?.gross || 0;
  const charged = (goods?.gross || 0) + (delivery?.gross || 0) + groupShares;
  const fundedDelivery = summary?.kinds?.deliveryOffersFunded?.gross || 0;
  // The period the figures are FOR comes from the response, never from the
  // picker, so a card can never claim a window it is not showing.
  const shownFrom = summary?.from ? localDateStr(new Date(summary.from)) : range.from;
  const shownTo = summary?.to ? localDateStr(new Date(summary.to)) : range.to;
  const periodLabel = shownFrom === shownTo ? fmtDay(shownFrom) : `${fmtDay(shownFrom)} to ${fmtDay(shownTo)}`;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div>
      {/* Period and filters */}
      <div style={{ ...S.panel, padding: "16px 20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {QUICK.map(([key, label]) => {
            const r = quickRange(key);
            const active = r.from === range.from && r.to === range.to;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setRange(r); setPage(1); }}
                style={{ ...S.btnSm(active ? "action" : "ghost") }}
              >
                {label}
              </button>
            );
          })}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMid, marginLeft: 4 }}>
            From
            <input type="date" value={range.from} max={range.to} onChange={(e) => { setRange((r) => ({ ...r, from: e.target.value })); setPage(1); }} style={{ ...S.input, width: "auto", padding: "5px 8px" }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMid }}>
            To
            <input type="date" value={range.to} min={range.from} onChange={(e) => { setRange((r) => ({ ...r, to: e.target.value })); setPage(1); }} style={{ ...S.input, width: "auto", padding: "5px 8px" }} />
          </label>
          <AppButton size="sm" variant="quiet" onClick={load} loading={loading} style={{ marginLeft: "auto" }} title="Refresh">
            <FiRefreshCw size={12} /> Refresh
          </AppButton>
        </div>
      </div>

      {error && (
        <div style={{ ...S.panel, color: C.red, fontSize: 13 }}>{error}</div>
      )}

      {/* Headline tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard
          icon={<FiDownload size={18} color={C.teal} />}
          iconBg={C.tealSoft}
          label="Money in"
          loading={loading}
          value={fmtUGX(total?.gross)}
          sub={summary ? `${total?.count || 0} inflow${total?.count === 1 ? "" : "s"}, all rails, ${periodLabel}` : undefined}
          hint="See the payments"
          onClick={() => scrollTo("money-in-payments")}
        />
        <StatCard
          icon={<FiPercent size={18} color={C.orange} />}
          iconBg={C.orangeSoft}
          label="MTN kept"
          loading={loading}
          value={fmtUGX(momo?.fee)}
          sub={summary ? `${summary.ratePct}% of MoMo collections${estimatedCount ? `, ${estimatedCount} estimated` : ""}` : undefined}
          hint="By kind and rail"
          onClick={() => scrollTo("money-in-sources")}
        />
        <StatCard
          icon={<FiDownload size={18} color={C.blue} />}
          iconBg={C.blueSoft}
          label="Landed in MTN wallet"
          loading={loading}
          value={fmtUGX(momo?.net)}
          sub={summary ? `of ${fmtUGX(momo?.gross)} sent to MTN` : undefined}
          hint="By kind and rail"
          onClick={() => scrollTo("money-in-sources")}
        />
        <StatCard
          icon={<FiDownload size={18} color={C.purple} />}
          iconBg={C.purpleSoft}
          label="Paid by customers"
          loading={loading}
          value={fmtUGX(charged)}
          sub={summary
            ? `goods ${fmtUGX(goods?.gross)}, delivery ${fmtUGX(delivery?.gross)}` +
              (groupShares ? `, group order shares ${fmtUGX(groupShares)}` : "") +
              (fundedDelivery ? `; a further ${fmtUGX(fundedDelivery)} of delivery funded by Lusuku` : "")
            : undefined}
          hint="See the payments"
          onClick={() => scrollTo("money-in-payments")}
        />
      </div>

      {/* Wallet movements the books do not explain. The ledger is what the
          tables below read; when the MTN wallet grew and no one recorded it
          (a float top-up never entered), this is the only place that says so,
          and the fix is one prefilled step away. */}
      {unexplained?.movements?.length > 0 && (
        <div style={{ ...S.panel, border: `1px solid ${C.orange}55`, background: C.orangeSoft }}>
          <div style={{ ...S.panelTitle, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            In the MTN wallet but not in the books
            <span style={{ fontSize: 12, fontWeight: 400, color: C.textMid }}>
              {unexplained.totalIn > 0 && `${fmtUGX(unexplained.totalIn)} arrived`}
              {unexplained.totalIn > 0 && unexplained.totalOut > 0 && " · "}
              {unexplained.totalOut > 0 && `${fmtUGX(unexplained.totalOut)} left`}
              {" "}with no record. The nightly reconciliation measured it from the wallet balance.
            </span>
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Day</th>
                <th style={S.th}>Wallet moved</th>
                <th style={{ ...S.th, textAlign: "right" }}>Amount (UGX)</th>
                <th style={S.th}>Flagged as</th>
                <th style={{ ...S.th, textAlign: "right" }}>Next step</th>
              </tr>
            </thead>
            <tbody>
              {unexplained.movements.map((m) => (
                <tr key={m.reportId || m.day}>
                  <td style={S.td}>{fmtDay(m.day)}</td>
                  <td style={S.td}>{fmtNum(m.opening)} to {fmtNum(m.closing)}</td>
                  <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong, color: m.gap > 0 ? C.green : C.red }}>{m.gap > 0 ? "+" : "-"}{fmtNum(Math.abs(m.gap))}</td>
                  <td style={S.td}>{m.discrepancyTypes.length ? m.discrepancyTypes.map((t) => t.replace(/_/g, " ").toLowerCase()).join(", ") : "below the reconciliation threshold"}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    {m.gap > 0 && onRecordTopUp ? (
                      <AppButton size="sm" onClick={() => onRecordTopUp({ amount: m.gap, occurredAt: m.periodEnd, coversDay: m.day, note: `MTN wallet grew ${fmtUGX(m.opening)} to ${fmtUGX(m.closing)} on ${fmtDay(m.day)}` })}>
                        Record as float top-up
                      </AppButton>
                    ) : (
                      <span style={{ fontSize: 12, color: C.textMid }}>Explain on the Reconciliation tab</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By kind and rail */}
      <div style={S.panel} id="money-in-sources">
        <div style={S.panelTitle}>Where the money came from</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Inflow</th>
              <th style={{ ...S.th, textAlign: "right" }}>Count</th>
              <th style={{ ...S.th, textAlign: "right" }}>Gross (UGX)</th>
              <th style={{ ...S.th, textAlign: "right" }}>Provider fee (UGX)</th>
              <th style={{ ...S.th, textAlign: "right" }}>Landed (UGX)</th>
            </tr>
          </thead>
          <tbody style={{ opacity: loading && rows.length ? 0.5 : 1, transition: "opacity 0.2s" }}>
            {rows.length === 0 && (
              loading ? <SkeletonRows columns={5} rows={4} /> : <tr><td colSpan={5} style={{ ...S.td, color: C.textLight }}>No money came in during this period.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} style={r.breakdown ? { color: C.textLight, fontSize: 12 } : undefined}>
                <td style={{ ...S.td, ...(r.group ? S.cellStrong : {}), paddingLeft: r.indent ? 32 : 14 }}>{r.label}</td>
                <td style={{ ...S.td, ...S.cellNum }}>{r.count}</td>
                <td style={{ ...S.td, ...S.cellNum }}>{fmtNum(r.gross)}</td>
                <td style={{ ...S.td, ...S.cellNum, color: r.fee ? C.orange : C.textLight }}>{r.fee ? fmtNum(r.fee) : "none"}</td>
                <td style={{ ...S.td, ...S.cellNum, ...(r.breakdown ? {} : S.cellStrong) }}>{r.nonCash ? "not money in" : fmtNum(r.net)}</td>
              </tr>
            ))}
            {momo && (
              <tr style={{ background: C.inputBg }}>
                <td style={{ ...S.td, ...S.cellStrong }}>Reached the MTN wallet</td>
                <td style={{ ...S.td, ...S.cellNum }}>{momo.count}</td>
                <td style={{ ...S.td, ...S.cellNum }}>{fmtNum(momo.gross)}</td>
                <td style={{ ...S.td, ...S.cellNum, color: C.orange }}>{fmtNum(momo.fee)}</td>
                <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtNum(momo.net)}</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginTop: 12, fontSize: 12, color: C.textMid }}>
          <span>{tariffNote(summary?.tariff)}</span>
          {isSuperAdmin && (
            <button type="button" style={S.btnSm("action")} onClick={openTariff}>Edit tariff</button>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.textLight, marginTop: 6 }}>
          Flutterwave money settles to the bank and is topped up into the float by hand; cash on delivery is held by riders until deposited. Neither touches the MTN wallet directly.
        </div>
      </div>

      {/* Customer payments */}
      <div style={S.panel} id="money-in-payments">
        <div style={{ ...S.panelTitle, justifyContent: "space-between" }}>
          <span>Customer payments</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: C.textLight }}>{pagination.total} total</span>
        </div>
        <form onSubmit={submitSearch} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <div style={{ position: "relative", flex: "1 1 260px" }}>
            <FiSearch size={13} style={{ position: "absolute", left: 10, top: 11, color: C.textLight }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order, vendor or customer id, MTN reference, or the number that paid"
              style={{ ...S.input, paddingLeft: 30 }}
            />
          </div>
          <select value={rail} onChange={(e) => { setRail(e.target.value); setPage(1); }} style={{ ...S.input, width: "auto" }}>
            <option value="">All rails</option>
            <option value="MOMO">MTN MoMo</option>
            <option value="FLW">Flutterwave</option>
            <option value="COD">Cash on delivery</option>
            <option value="UNTAGGED">Not tagged</option>
          </select>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ ...S.input, width: "auto" }}>
            <option value="">Any escrow status</option>
            <option value="HELD">Held</option>
            <option value="RELEASED">Released</option>
            <option value="REVERSED">Reversed</option>
          </select>
          <button type="submit" style={S.btn("action")}>Search</button>
        </form>
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Paid</th>
                <th style={S.th}>From</th>
                <th style={S.th}>Order</th>
                <th style={S.th}>Vendor</th>
                <th style={S.th}>Rail</th>
                <th style={{ ...S.th, textAlign: "right" }}>Charged (UGX)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Provider fee (UGX)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Landed (UGX)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Commission (UGX)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Vendor net (UGX)</th>
                <th style={S.th}>Escrow</th>
              </tr>
            </thead>
            <tbody style={{ opacity: loading && list.records.length ? 0.5 : 1, transition: "opacity 0.2s" }}>
              {list.records.length === 0 && (
                loading ? <SkeletonRows columns={11} rows={3} /> : <tr><td colSpan={11} style={{ ...S.td, color: C.textLight }}>No customer payments match.</td></tr>
              )}
              {list.records.map((r) => {
                const contract = vendorName(r.vendorId);
                const railKey = r.rail || "UNTAGGED";
                return (
                  <tr key={r.id}>
                    {/* When the provider confirmed the money; the hold time
                        only for rows booked before paidAt was recorded. */}
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      <CellStack meta={r.paidAt && r.createdAt && new Date(r.createdAt) - new Date(r.paidAt) > 5 * 60 * 1000 ? `booked ${fmtDay(r.createdAt)} ${fmtClock(r.createdAt)}` : null}>
                        <DateTimeCell value={r.paidAt || r.createdAt} />
                      </CellStack>
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>{r.payerMsisdn ? formatMsisdn(r.payerMsisdn) : <span style={{ color: C.textLight }}>not recorded</span>}</td>
                    <td style={S.td}>
                      {r.orderId ? (
                        <CopyValue value={r.orderId} max={12} strong>
                          <Link to={`/admin/orderDetails/${r.orderId}`} title={r.orderId} style={{ color: C.action, textDecoration: "none", fontWeight: 600 }}>{truncate(r.orderId, 12)}</Link>
                        </CopyValue>
                      ) : "n/a"}
                      {r.reference && (
                        <div style={S.cellMeta} title={r.reference}>
                          <CopyValue value={r.reference} max={14} label="MTN ref" style={{ fontSize: 11 }} />
                        </div>
                      )}
                    </td>
                    <td style={S.td}>
                      <VendorCell name={contract?.name || truncate(r.vendorId, 14)} type={r.vendorType} country={contract?.country} />
                    </td>
                    <td style={S.td}><span style={S.badge(RAIL_TONE[railKey])}>{RAIL_LABEL[railKey]}</span></td>
                    {/* The charge as the customer saw it: goods plus the
                        delivery fee paid with them. Fee and landed below are
                        the whole payment's too when a delivery fee is on the row. */}
                    <td style={{ ...S.td, ...S.cellNum }}>
                      <CellStack
                        style={{ textAlign: "right" }}
                        meta={
                          r.deliveryFee || r.deliveryFeeFundedByLusuku
                            ? `goods ${fmtNum(r.gross)} + delivery ${fmtNum(r.deliveryFee || 0)}` +
                              (r.deliveryFeeFundedByLusuku ? `; ${fmtNum(r.deliveryFeeFundedByLusuku)} of delivery funded by Lusuku` : "")
                            : null
                        }
                      >
                        {fmtNum(r.gross + (r.deliveryFee || 0))}
                      </CellStack>
                    </td>
                    <td style={{ ...S.td, ...S.cellNum }}>
                      <CellStack
                        style={{ textAlign: "right" }}
                        meta={FEE_BASIS_LABEL[r.feeBasis] + (r.providerFeeRate != null && r.feeBasis !== "NONE" ? ` at ${r.providerFeeRate}%` : "")}
                        metaColor={r.feeBasis === "ESTIMATED" ? C.orange : undefined}
                      >
                        <span style={{ color: r.providerFee || r.deliveryFeeProviderFee ? C.orange : C.textLight }}>
                          {r.providerFee || r.deliveryFeeProviderFee ? fmtNum((r.providerFee || 0) + (r.deliveryFeeProviderFee || 0)) : "none"}
                        </span>
                      </CellStack>
                    </td>
                    <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtNum(r.netLanded + (r.deliveryFee || 0) - (r.deliveryFeeProviderFee || 0))}</td>
                    <td style={{ ...S.td, ...S.cellNum }}>
                      <CellStack style={{ textAlign: "right" }} meta={r.commissionRate != null ? `${r.commissionRate}%` : null}>{fmtNum(r.commission)}</CellStack>
                    </td>
                    <td style={{ ...S.td, ...S.cellNum }}>{fmtNum(r.vendorNet)}</td>
                    <td style={S.td}>
                      <CellStack meta={r.releasedAt ? <DateTimeCell value={r.releasedAt} /> : null}>
                        <span style={S.badge(ESCROW_TONE[r.status] || "blue")}>{r.status}</span>
                      </CellStack>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12, color: C.textMid }}>
            <button type="button" style={S.btnSm("ghost")} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {pagination.page} of {pagination.pages}</span>
            <button type="button" style={S.btnSm("ghost")} disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* MTN tariff */}
      <Modal show={tariffModal} onHide={() => !tariffSaving && setTariffModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>MTN collection tariff<SettingHelp topic="mtnCollectionFee" placement="bottom" /></Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          <p style={{ color: C.textMid, marginTop: 0 }}>
            The percentage MTN keeps out of every MoMo collection (customer payments and rider deposits)
            before the money reaches the wallet. The wallet books it as an MTN fee on each inflow
            recorded from now on, and the nightly reconciliation expects the balance net of it. MTN
            does not publish this figure on the API; it comes from the merchant contract and was
            measured at 2%, the shipped default. Change it only after the reconciliation shows a
            different deduction on several days, and set the MTN row of the provider rate card under
            Settings, Payment rail fees to the same figure.
          </p>
          <label style={{ display: "block", marginBottom: 10 }}>
            Percent kept by MTN
            <input
              type="number" min="0" max="100" step="0.1"
              value={tariffForm.ratePct}
              onChange={(e) => setTariffForm((f) => ({ ...f, ratePct: e.target.value }))}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 0 }}>
            Reason for the change (kept in the audit log)
            <input
              type="text"
              value={tariffForm.reason}
              onChange={(e) => setTariffForm((f) => ({ ...f, reason: e.target.value }))}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setTariffModal(false)} disabled={tariffSaving}>Cancel</Button>
          <Button size="sm" onClick={saveTariff} disabled={tariffSaving} style={{ background: C.action, border: "none" }}>
            {tariffSaving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
